/**
 * One favourite list (DEV-51010 AC10–AC13), e.g. "Tue EV job".
 *
 * - Header: list name (one line, truncated) and "N items", with a menu
 *   offering Rename and Delete.
 * - Rows: the list's favourites, newest first, each with the heart and
 *   "Add to list". Unticking this list in the sheet removes the row.
 * - Empty list: how to add items, with a "Search products" button.
 * - Unknown or deleted list id: "This list doesn't exist" with "Back to lists".
 *
 * Back returns through history when the engineer came from the Lists tab,
 * so Back never lands on a list they just deleted. From a shared link it
 * replaces itself with the Lists tab instead.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import * as Dialog from '@radix-ui/react-dialog'
import { AlertCircle, ArrowLeft, ListChecks, SearchX } from 'lucide-react'

import { EngineerLayout } from '../../components/layout/EngineerLayout'
import { Button, EmptyState, OverflowMenu, useToast } from '../../components/radix'
import { FavouriteRow } from '../../components/engineer/favourites/FavouriteRow'
import { ListNameDialog } from '../../components/engineer/favourites/ListNameDialog'
import { ListsSkeleton } from '../../components/engineer/favourites/ListsPanel'
import { useFavouriteLists } from '../../auth/hooks/useFavouriteLists'
import { useFavourites } from '../../auth/hooks/useFavourites'
import {
  cameFromLists,
  itemCountLabel,
  listsTabPath,
  saveListName,
} from '../../utils/favouriteLists'
import type { Favourite, FavouriteList } from '../../types'

export function FavouriteListPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { tenantSlug, listId } = useParams<{ tenantSlug: string; listId: string }>()
  const { addToast } = useToast()
  // Same postcode gate as the Favourites page: tiles resolve against it.
  const [hasSession] = useState(() => sessionStorage.getItem('engineerSession') !== null)
  const [renaming, setRenaming] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (!hasSession) navigate(`/${tenantSlug}/engineer`, { replace: true })
  }, [hasSession, navigate, tenantSlug])

  const { lists, isLoading: listsLoading, isError, refetch, renameList, deleteList } =
    useFavouriteLists()
  const {
    favourites,
    isLoading: favouritesLoading,
    isError: favouritesError,
    refetch: refetchFavourites,
  } = useFavourites()

  const list = lists.find((candidate) => candidate.id === listId)
  const items = useMemo(() => {
    if (!list) return []
    const byId = new Map(favourites.map((favourite) => [favourite.id, favourite]))
    return list.favouriteIds
      .map((id) => byId.get(id))
      .filter((favourite): favourite is Favourite => favourite !== undefined)
  }, [list, favourites])

  const backToLists = () => {
    if (cameFromLists(location.state)) navigate(-1)
    else navigate(listsTabPath(tenantSlug), { replace: true })
  }

  const confirmDelete = (target: FavouriteList) => {
    setDeleting(true)
    // Optimistic: the Lists tab is already without it when we land there.
    deleteList(target.id).catch(() => {
      addToast(`Couldn't delete ${target.name}. Try again.`, 'error')
    })
    backToLists()
  }

  if (!hasSession) return null

  const header = (
    <div className="bg-nav text-nav border-b border-nav-divider sticky top-0 z-20">
      <div className="max-w-3xl mx-auto px-4 py-2 flex items-center gap-2">
        <button
          onClick={backToLists}
          aria-label="Back to lists"
          className="flex h-11 w-11 -ml-3 flex-shrink-0 items-center justify-center rounded-xl hover:bg-nav-hover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
        >
          <ArrowLeft className="w-5 h-5" aria-hidden="true" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="font-bold truncate">{list ? list.name : 'List'}</h1>
          {list && <p className="text-xs opacity-70">{itemCountLabel(items.length)}</p>}
        </div>
        {list && (
          <OverflowMenu
            label="List options"
            triggerClassName="-mr-3"
            items={[
              { label: 'Rename', onSelect: () => setRenaming(true) },
              { label: 'Delete', onSelect: () => setConfirmingDelete(true), destructive: true },
            ]}
          />
        )}
      </div>
    </div>
  )

  if (listsLoading || favouritesLoading) {
    return (
      <EngineerLayout hideHeader>
        {header}
        <div className="max-w-3xl mx-auto px-4 py-5">
          <ListsSkeleton />
        </div>
      </EngineerLayout>
    )
  }

  // Rows come from the favourites query, so if it failed the list would
  // wrongly read as empty (and Delete would say so).
  if (isError || favouritesError) {
    return (
      <EngineerLayout hideHeader>
        {header}
        <div className="max-w-3xl mx-auto px-4">
          <EmptyState
            icon={<AlertCircle className="h-10 w-10 text-gray-400" aria-hidden="true" />}
            title="Couldn't load this list"
            description="Check your connection and try again."
            action={
              <Button
                onClick={() => {
                  if (isError) refetch()
                  if (favouritesError) refetchFavourites()
                }}
              >
                Try again
              </Button>
            }
          />
        </div>
      </EngineerLayout>
    )
  }

  if (!list) {
    // Just deleted from this screen: render nothing while navigating back,
    // rather than flashing "doesn't exist".
    if (deleting) return null
    return (
      <EngineerLayout hideHeader>
        {header}
        <div className="max-w-3xl mx-auto px-4">
          <EmptyState
            icon={<SearchX className="h-10 w-10 text-gray-400" aria-hidden="true" />}
            title="This list doesn't exist"
            description="It may have been deleted, or the link is wrong."
            action={<Button onClick={backToLists}>Back to lists</Button>}
          />
        </div>
      </EngineerLayout>
    )
  }

  return (
    <EngineerLayout hideHeader>
      {header}

      <div className="max-w-3xl mx-auto px-4 py-5 pb-24">
        {items.length === 0 ? (
          <EmptyState
            className="py-10"
            icon={<ListChecks className="h-10 w-10 text-brand-primary" aria-hidden="true" />}
            title="No items in this list"
            description="Tap the heart on a product to save it, then choose Add to list."
            action={
              <Button onClick={() => navigate(`/${tenantSlug}/engineer/search`)}>
                Search products
              </Button>
            }
          />
        ) : (
          <ul className="rounded-xl border border-gray-200 bg-white overflow-hidden divide-y divide-gray-100">
            {items.map((favourite) => (
              <li key={favourite.id}>
                <FavouriteRow favourite={favourite} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <ListNameDialog
        open={renaming}
        onOpenChange={setRenaming}
        title="Rename list"
        submitLabel="Save"
        initialName={list.name}
        onSubmit={async (name) => {
          const result = await saveListName(
            name,
            lists,
            (value) => renameList(list.id, value),
            list.id,
          )
          return result.ok ? null : result.error
        }}
      />

      <DeleteListDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        list={list}
        itemCount={items.length}
        onConfirm={() => confirmDelete(list)}
      />
    </EngineerLayout>
  )
}

/**
 * "Delete Tue EV job?" confirmation (AC12). Focus starts on Cancel so a
 * stray Enter doesn't delete.
 */
function DeleteListDialog({
  open,
  onOpenChange,
  list,
  itemCount,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  list: FavouriteList
  itemCount: number
  onConfirm: () => void
}) {
  const cancelRef = useRef<HTMLButtonElement>(null)

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="animate-fade-in fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            cancelRef.current?.focus()
          }}
          className="fixed left-1/2 top-1/2 z-[51] max-h-[85vh] w-[calc(100vw-24px)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border border-gray-200 bg-white p-5 shadow-xl focus:outline-none sm:p-6"
        >
          <Dialog.Title className="break-words pr-8 text-xl font-bold text-gray-900">
            Delete {list.name}?
          </Dialog.Title>
          <Dialog.Description className="mt-2 text-base text-gray-500">
            {itemCount === 0
              ? 'The list is empty, so nothing else changes.'
              : `The ${itemCountLabel(itemCount)} ${itemCount === 1 ? 'stays' : 'stay'} in your favourites.`}
          </Dialog.Description>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <Button variant="danger" className="sm:flex-1" onClick={onConfirm}>
              Delete list
            </Button>
            <Button
              ref={cancelRef}
              variant="neutral"
              className="sm:flex-1"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
